import {Box} from '@mui/material';
import {useEffect} from 'react';
import {useTranslation} from 'react-i18next';
import {useHistory, useParams, useRouteMatch} from 'react-router-dom';
import GenericHeader from '../../../../../../../components/generic-header/generic-header';
import {PlaylistItemForm} from '../../../../../../../components/playlist-form/playlist-item-form';
import {SectionTabs} from '../../../../../../../components/section-tabs/section-tabs';
import {useOffice} from '../../../../../../../contexts/office/context';
import {useSnackBar} from '../../../../../../../contexts/snack-bar/context';
import {CaregiverMode, ScreenPaths} from '../../../../../../../enums';
import {translations} from '../../../../../../../i18n/translations';
import {PlaylistsRepository} from '../../../../../../../repositories/playlists';

export default function PlaylistItemDetailsProfessionalCaregiverOfficeScreen({
  canWrite,
}: {
  canWrite?: boolean;
}) {
  // Get params
  // - id: playlist id
  // - entityId: entity id
  // - itemId: playlist item id
  let {entityId, playlistId, itemId}: {entityId: string; playlistId: string; itemId: string} =
    useParams();

  const {t} = useTranslation();

  const officeContext = useOffice();
  const snackBarContext = useSnackBar();
  const history = useHistory();

  // The `path` lets us build <Route> paths that are
  // relative to the parent route, while the `url` lets
  // us build relative links.
  let {url} = useRouteMatch();

  // Validate and get the playlist item
  useEffect(() => {
    const getPlaylistItem = async () => {
      try {
        // Check ids
        if (!playlistId || !entityId || !itemId) {
          // Error
          throw new Error('Empty entity, playlist, or playlist item id.');
        }

        // Get from backend
        // Note: Don't localize because we need the original model for the playlist item form
        const result =
          await PlaylistsRepository.instance.getPlaylistItemByIdForEntityAsAffiliatedForEntity(
            entityId,
            playlistId,
            itemId,
            false
          );

        if (result) {
          // Set playlist item in context
          officeContext.setPlaylistItemForm(result);
        } else {
          // Error
          throw new Error('Empty playlist item.');
        }
      } catch (error) {
        // Log
        console.debug('🔴 Could not get the playlist item.', error);

        // Show toast error
        snackBarContext.setOfficeToastNotification({
          severity: 'error',
          message: t(
            translations.playlists.professional_caregiver.playlist_item.errors
              .item_does_not_exist_error
          ),
        });

        // Redirect
        history.push(`${url.replace(`/items/${itemId}`, '/items')}`);
      }
    };

    // Get playlist item
    getPlaylistItem();

    // // Clean up
    // return () => {
    //   // Clear playlist item
    //   // officeContext.clearPlaylistItemForm();

    //   // Log
    //   console.debug('🧹 Playlist item form cleaned in office context.');
    // };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [itemId]);

  const baseUrl = `${ScreenPaths.Office}${ScreenPaths.Office_ProfessionalCaregiver}/${entityId}${ScreenPaths.ProfessionalCaregiver_DigitalInclusion}`;
  const playlistUrl = `${baseUrl}${ScreenPaths.ProfessionalCaregiver_DigitalInclusion_Playlists}/${playlistId}`;
  const playlistItemUrl = `${playlistUrl}/items/${itemId}`;

  return (
    <>
      <GenericHeader
        isLoading={!officeContext.state.playlistForm || !officeContext.state.playlistItemForm}
        titleCrumbs={[
          {
            label: t(translations.office.professional_caregiver.tabs.digital_inclusion),
            link: baseUrl,
          },
          {
            label: t(translations.office.professional_caregiver.tabs.playlists),
            link: `${baseUrl}${ScreenPaths.ProfessionalCaregiver_DigitalInclusion_Playlists}`,
          },
          {
            label: officeContext.state.playlistForm?.name || '',
            link: playlistUrl,
          },
          {
            label: officeContext.state.playlistItemForm?.title || '',
            link: playlistItemUrl,
          },
          {
            label: t(translations.playlists.professional_caregiver.edit.tabs.details),
            link: url,
          },
        ]}
        leftColumn={null}
        rightColumn={null}
      />

      <Box className="listing-container">
        <Box className="listing-container-centered">
          <Box sx={{backgroundColor: 'white', paddingBottom: '16px'}}>
            <SectionTabs
              currentTab="details"
              tabs={[
                {
                  value: 'details',
                  label: t(
                    translations.playlists.professional_caregiver.playlist_item.tabs.details
                  ),
                  href: `${playlistItemUrl}/details`,
                },
                {
                  value: 'users',
                  label: t(translations.playlists.professional_caregiver.playlist_item.tabs.users),
                  href: `${playlistItemUrl}/users`,
                },
              ]}
              exitUrl={`${url.replace(`/items/${itemId}`, '/items')}`}
            />
          </Box>
          <PlaylistItemForm
            caregiverMode={CaregiverMode.Professional}
            canWrite={canWrite || false}
            showExitButton={false}
            showPageTitle={false}
            redirectUrl={`${url.replace(`/playlists/${playlistId}/items/${itemId}`, '')}`}
            entityId={entityId}
          />
        </Box>
      </Box>
    </>
  );
}
